import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { skipToken } from '@reduxjs/toolkit/query';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell,
} from 'recharts';
import {
  useGetNationalHolisticAnalyticsQuery,
  mergeNationalAnalyticsResponses,
  TermFilter,
  termFilterToQueryValue,
  getClassDisplayName,
  useGetHpcYearsQuery,
} from '../../../services/holisticApi';
import KVSLoader from '../../../../../components/loaders/KVSLoader';
import { StatBox } from '../../../components/shared';

const COLORS = ['#6366F1', '#818CF8', '#A5B4FC', '#C7D2FE'];

const scaleToPercent = (val: number): number => {
  if (val <= 1.0 && val > 0) return +(val * 100).toFixed(1);
  return +val.toFixed(1);
};

const InterestsAnalysis: React.FC = () => {
  const navigate = useNavigate();
  const { data: yearsRes } = useGetHpcYearsQuery();
  const availableYears = useMemo(() => {
    return (yearsRes?.data || [2026, 2025]).map(Number);
  }, [yearsRes]);

  const [academicYear, setAcademicYear] = useState<number>(2025);

  React.useEffect(() => {
    if (yearsRes?.data?.length) {
      const numericYears = yearsRes.data.map(Number);
      if (!numericYears.includes(Number(academicYear))) {
        setAcademicYear(numericYears[0]);
      }
    }
  }, [yearsRes, academicYear]);

  const [term, setTerm] = useState<TermFilter>('overall');
  const isOverall = term === 'overall';

  const singleTermQuery = useGetNationalHolisticAnalyticsQuery(
    !isOverall ? { academicYear, term } : skipToken,
  );
  const term1Query = useGetNationalHolisticAnalyticsQuery(
    isOverall ? { academicYear, term: 1 } : skipToken,
  );
  const term2Query = useGetNationalHolisticAnalyticsQuery(
    isOverall ? { academicYear, term: 2 } : skipToken,
  );

  const effectiveData = useMemo(() => {
    if (!isOverall) return singleTermQuery.data;
    return mergeNationalAnalyticsResponses(term1Query.data, term2Query.data);
  }, [isOverall, singleTermQuery.data, term1Query.data, term2Query.data]);

  const isLoading = isOverall ? (term1Query.isLoading || term2Query.isLoading) : singleTermQuery.isLoading;

  const records = useMemo(() => {
    if (!effectiveData?.data) return [];
    return effectiveData.data.flatMap((region) => region.records || []);
  }, [effectiveData]);



  const stats = useMemo(() => {
    if (!records.length) {
      return {
        avgAesthetic: 0,
        avgParticipation: 0,
        evaluated: 0,
        aestheticDistribution: [
          { category: 'Excellent (>= 85%)', count: 0 },
          { category: 'Good (70-84%)', count: 0 },
          { category: 'Average (55-69%)', count: 0 },
          { category: 'Below Average (< 55%)', count: 0 },
        ],
        participationDistribution: [
          { category: 'Excellent (>= 85%)', count: 0 },
          { category: 'Good (70-84%)', count: 0 },
          { category: 'Average (55-69%)', count: 0 },
          { category: 'Below Average (< 55%)', count: 0 },
        ],
        studentsList: [],
      };
    }

    let totalAesthetic = 0;
    let totalParticipation = 0;

    let aesExcellent = 0;
    let aesGood = 0;
    let aesAverage = 0;
    let aesBelow = 0;

    let partExcellent = 0;
    let partGood = 0;
    let partAverage = 0;
    let partBelow = 0;

    const list = records.map((record) => {
      const aesScore = scaleToPercent(Number(record.aesthetic_score || 0));
      const partScore = scaleToPercent(Number(record.participation_score || 0));

      totalAesthetic += aesScore;
      totalParticipation += partScore;

      if (aesScore >= 85) aesExcellent++;
      else if (aesScore >= 70) aesGood++;
      else if (aesScore >= 55) aesAverage++;
      else aesBelow++;

      if (partScore >= 85) partExcellent++;
      else if (partScore >= 70) partGood++;
      else if (partScore >= 55) partAverage++;
      else partBelow++;

      const first = (record.firstName ?? record.first_name ?? '').trim();
      const middle = (record.middleName ?? record.middle_name ?? '').trim();
      const last = (record.lastName ?? record.last_name ?? '').trim();
      const fullName = [first, middle, last].filter(Boolean).join(' ') || `Student ${record.student_id}`;

      return {
        id: record.student_id,
        name: fullName,
        className: getClassDisplayName(Number(record.class_id)),
        section: '-',
        aestheticScore: aesScore,
        participationScore: partScore,
        regionId: record.region_id,
        schoolId: record.school_id,
        classId: record.class_id,
      };
    });

    const len = records.length;
    return {
      avgAesthetic: +(totalAesthetic / len).toFixed(1),
      avgParticipation: +(totalParticipation / len).toFixed(1),
      evaluated: len,
      aestheticDistribution: [
        { category: 'Excellent (>= 85%)', count: aesExcellent },
        { category: 'Good (70-84%)', count: aesGood },
        { category: 'Average (55-69%)', count: aesAverage },
        { category: 'Below Average (< 55%)', count: aesBelow },
      ],
      participationDistribution: [
        { category: 'Excellent (>= 85%)', count: partExcellent },
        { category: 'Good (70-84%)', count: partGood },
        { category: 'Average (55-69%)', count: partAverage },
        { category: 'Below Average (< 55%)', count: partBelow },
      ],
      studentsList: list.slice(0, 50),
    };
  }, [records]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[320px]">
        <KVSLoader />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-[1600px] mx-auto space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-gray-900 mb-1">Interests & Lifestyle Analysis</h1>
          <p className="text-sm text-gray-500">Aesthetic development and participation indicators across {records.length} students</p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2">
            <label htmlFor="term" className="text-xs font-medium text-gray-600">Term</label>
            <select
              id="term"
              value={term}
              onChange={(e) => {
                const value = e.target.value;
                setTerm(value === 'overall' ? 'overall' : value === '2' ? 2 : 1);
              }}
              className="text-sm text-gray-700 bg-transparent focus:outline-none"
            >
              <option value="overall">Overall</option>
              <option value="1">Term 1</option>
              <option value="2">Term 2</option>
            </select>
          </div>

          <div className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2">
            <label htmlFor="academicYear" className="text-xs font-medium text-gray-600">Academic Session</label>
            <select
              id="academicYear"
              value={academicYear}
              onChange={(e) => setAcademicYear(Number(e.target.value))}
              className="text-sm text-gray-700 bg-transparent focus:outline-none"
            >
              {availableYears.map((year: number) => (
                <option key={year} value={year}>
                  {year}-{String(Number(year) + 1).slice(-2)}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4 mb-6">
        <StatBox label="Avg Aesthetic Score" value={`${stats.avgAesthetic}%`} color="text-indigo-600" large />
        <StatBox label="Avg Participation Score" value={`${stats.avgParticipation}%`} color="text-blue-600" large />
        <StatBox label="Students Evaluated" value={`${stats.evaluated}`} color="text-emerald-600" large />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">Aesthetic Development Distribution</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={stats.aestheticDistribution} barSize={40}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
              <XAxis dataKey="category" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ borderRadius: 8, fontSize: 13 }} />
              <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                {stats.aestheticDistribution.map((entry, idx) => (
                  <Cell key={entry.category} fill={COLORS[idx % COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">Participation & Engagement Distribution</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={stats.participationDistribution} barSize={40}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
              <XAxis dataKey="category" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ borderRadius: 8, fontSize: 13 }} />
              <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                {stats.participationDistribution.map((entry, idx) => (
                  <Cell key={entry.category} fill={COLORS[idx % COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Student List */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-700">Student Interests & Lifestyle Metrics</h3>
          <span className="text-xs text-gray-400">Showing first 50 students</span>
        </div>
        <div className="divide-y divide-gray-100">
          {stats.studentsList.map((student: any) => (
            <div
              key={student.id}
              onClick={() => navigate(`/kvs-samagam/student-analytics/region/${student.regionId}/school/${student.schoolId}/class/${student.classId}/students/${student.id}/interests?year=${academicYear}&term=${termFilterToQueryValue(term)}`)}
              className="px-6 py-4 hover:bg-indigo-50/40 cursor-pointer transition flex items-center justify-between"
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-xs font-bold">
                  {student.name.split(' ').map((n: string) => n[0]).join('')}
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">{student.name}</p>
                  <p className="text-xs text-gray-400">{student.className}</p>
                </div>
              </div>
              <div className="flex items-center gap-6">
                <div className="text-right hidden md:block">
                  <p className="text-[10px] text-gray-400 uppercase">Aesthetic / Participation</p>
                  <p className="text-xs font-semibold text-gray-700">{student.aestheticScore}% / {student.participationScore}%</p>
                </div>
                <span className="text-xs text-indigo-600 font-semibold hover:underline">
                  View Interests & Choices →
                </span>
              </div>
            </div>
          ))}
          {stats.studentsList.length === 0 && (
            <div className="px-6 py-8 text-center text-sm text-gray-400">No data available</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InterestsAnalysis;
